<template>
    <div class="lookup-cell" :class="[field.alignment, { 'with-tooltip': showTooltip }]">
        <a-tooltip :title="tooltipContent" :visible="showTooltip" @visibleChange="handleTooltipVisibility">
            <div class="cell-content" @mouseenter="showTooltip = true" @mouseleave="showTooltip = false">
                <span v-if="loading" class="loading-indicator">
                    <a-icon type="loading" />
                </span>
                <template v-else>
                    <span class="value">{{ formattedValue }}</span>
                    <span class="lookup-badge">
                        <i class="material-icons">sync_alt</i>
                    </span>
                </template>
            </div>
        </a-tooltip>
    </div>
</template>

<script>
import Vue from "vue";
import { isNumber, isDate } from "lodash";
import moment from "moment";
import { mapState } from "vuex";

export default Vue.extend({
    name: "LookupCell",
    props: {
        record: {
            type: Object,
            required: true,
        },
        field: {
            type: Object,
            required: true,
        },
        readonly: {
            type: Boolean,
            default: true,
        },
        outlined: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            loading: false,
            showTooltip: false,
        };
    },
    computed: {
        ...mapState({
            tables: (state) => state.table.tables,
        }),
        rawValue() {
            return this.record.data[this.field._id];
        },
        formattedValue() {
            if (this.rawValue === null || this.rawValue === undefined) {
                return "";
            }

            // Tarih ise, formatla
            if (this.isDateValue(this.rawValue)) {
                return moment(this.rawValue).format("DD.MM.YYYY HH:mm");
            }

            // Sayı ise, formatla
            if (isNumber(this.rawValue)) {
                return new Intl.NumberFormat("tr-TR", {
                    maximumFractionDigits: 2,
                    minimumFractionDigits: 0,
                }).format(this.rawValue);
            }

            // Boolean ise, Evet/Hayır olarak göster
            if (typeof this.rawValue === "boolean") {
                return this.rawValue ? "Evet" : "Hayır";
            }

            // Diğer türdeki değerler için
            return String(this.rawValue);
        },
        tooltipContent() {
            if (!this.field.options || !this.field.options.lookup) {
                return "Lookup Alan Bilgisi Bulunamadı";
            }

            const linkedRecordFieldId = this.field.options.lookup.linkedRecordField;
            const targetFieldId = this.field.options.lookup.targetField;

            const linkedRecordField = this.findField(linkedRecordFieldId);
            const linkedRecordFieldName = linkedRecordField ? linkedRecordField.name : "Bilinmeyen Alan";

            // Bağlı tablodaki hedef alanı bulmak için
            const linkedTableId = linkedRecordField?.options?.linkedRecord?.table;
            const linkedTable = this.tables.find((t) => t._id === linkedTableId);

            let targetFieldName = "Bilinmeyen Alan";
            if (linkedTable) {
                const targetField = linkedTable.fields.find((f) => f._id === targetFieldId);
                if (targetField) {
                    targetFieldName = targetField.name;
                }
            }

            return `Lookup Alan: ${this.field.name}
Bağlantılı Kayıt Alanı: ${linkedRecordFieldName}
Hedef Alan: ${targetFieldName}
Değer: ${this.formattedValue}`;
        },
    },
    methods: {
        handleTooltipVisibility(visible) {
            this.showTooltip = visible;
        },
        findField(fieldId) {
            // Mevcut tablodaki alanları bulmak için
            const currentTable = this.tables.find((t) => t.fields.some((f) => f._id === this.field._id));

            return currentTable?.fields.find((f) => f._id === fieldId);
        },
        isDateValue(value) {
            if (!value) return false;

            // Tarih nesnesi ise
            if (value instanceof Date) return true;

            // String tarih ise kontrol et
            if (typeof value === "string") {
                const date = new Date(value);
                return !isNaN(date.getTime());
            }

            return false;
        },
    },
});
</script>

<style lang="scss" scoped>
.lookup-cell {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    position: relative;

    cursor: default; // Read-only olduğunu göstermek için

    &.left {
        justify-content: flex-start;
    }

    &.center {
        justify-content: center;
    }

    &.right {
        justify-content: flex-end;
    }

    &.with-tooltip {
        background-color: #f0f5ff;
    }

    .cell-content {
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        padding: 4px 8px;
        position: relative;

        .value {
            flex: 1;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }

        .lookup-badge {
            font-size: 12px;
            color: #1890ff;
            display: flex;
            align-items: center;
            margin-left: 6px;

            i {
                font-size: 16px;
            }
        }

        .loading-indicator {
            width: 100%;
            text-align: center;
            color: #d9d9d9;
        }
    }
}
</style>
